import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FaRegBuilding } from "react-icons/fa";
import { Button } from "@mui/material";
import { IoClose } from "react-icons/io5";
import moment from "moment";
import styles from "./ViewLessonPlanApproval.module.css";
import AxiosInstance, { TokenService } from "../../../../../../services/Api";

/* ==================================================================
   Inline styles for the Rejection Reason Modal
   (identical to the list page)
   ================================================================== */
const modalCss = {
  backdrop: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(30, 35, 50, 0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1300,
    padding: "16px",
  },
  modal: {
    background: "#ffffff",
    borderRadius: "10px",
    width: "100%",
    maxWidth: "520px",
    borderTop: "4px solid #f58220",
    boxShadow: "0 14px 40px rgba(0, 0, 0, 0.18)",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 20px 10px 20px",
  },
  title: {
    fontSize: "17px",
    fontWeight: 600,
    color: "#1f1f1f",
    margin: 0,
    letterSpacing: "0.2px",
  },
  closeBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: "#6b7280",
    fontSize: "22px",
    padding: "4px",
    lineHeight: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
  },
  body: {
    padding: "6px 20px 18px 20px",
  },
  inputLabel: {
    display: "block",
    fontSize: "13px",
    color: "#9a9a9a",
    fontWeight: 400,
    marginBottom: "6px",
  },
  input: {
    width: "100%",
    border: "none",
    borderBottom: "1px solid #d0d5dd",
    padding: "6px 0 8px 0",
    fontSize: "15px",
    color: "#1f1f1f",
    background: "transparent",
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  inputFocused: {
    borderBottom: "2px solid #f58220",
    paddingBottom: "7px",
  },
  inputError: {
    borderBottom: "2px solid #dc2626",
  },
  errorText: {
    color: "#dc2626",
    fontSize: "12.5px",
    marginTop: "6px",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "12px 20px 22px 20px",
  },
  btn: {
    minWidth: "130px",
    height: "40px",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    padding: "0 22px",
    fontFamily: "inherit",
  },
  cancelBtn: {
    backgroundColor: "#1a2b5a",
    color: "#ffffff",
  },
  confirmBtn: {
    backgroundColor: "#f58220",
    color: "#ffffff",
  },
  btnDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
};

/* ==================================================================
   Inline Rejection Reason Modal component
   (identical to the list page)
   ================================================================== */
const RejectionReasonModal = ({
  open,
  onClose,
  onConfirm,
  loading,
  translations,
}) => {
  const t = translations?.labels || {};
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason("");
      setError("");
      setFocused(false);
    }
  }, [open]);

  if (!open) return null;

  const handleConfirm = () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError(t.reasonRequired || "Please provide a reason for rejection");
      return;
    }
    if (typeof onConfirm === "function") {
      onConfirm(trimmed);
    }
  };

  const handleChange = (e) => {
    setReason(e.target.value);
    if (error) setError("");
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose?.();
    }
  };

  const inputStyle = {
    ...modalCss.input,
    ...(focused && !error ? modalCss.inputFocused : {}),
    ...(error ? modalCss.inputError : {}),
  };

  return (
    <div style={modalCss.backdrop} onMouseDown={handleBackdropClick}>
      <div
        style={modalCss.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rejection-reason-title"
      >
        {/* Header */}
        <div style={modalCss.header}>
          <h3 id="rejection-reason-title" style={modalCss.title}>
            {t.reasonForRejection || "Reason for Rejection"}
          </h3>
          <button
            type="button"
            style={{
              ...modalCss.closeBtn,
              ...(loading ? modalCss.btnDisabled : {}),
            }}
            onClick={onClose}
            disabled={loading}
            aria-label={t.close || "Close"}
          >
            <IoClose />
          </button>
        </div>

        {/* Body */}
        <div style={modalCss.body}>
          <label style={modalCss.inputLabel} htmlFor="rejection-reason-input">
            {t.reason || "Reason"}
          </label>
          <input
            id="rejection-reason-input"
            type="text"
            style={inputStyle}
            value={reason}
            onChange={handleChange}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={t.enterReasonPlaceholder || ""}
            disabled={loading}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") handleConfirm();
              if (e.key === "Escape") onClose?.();
            }}
          />
          {error && <div style={modalCss.errorText}>{error}</div>}
        </div>

        {/* Footer */}
        <div style={modalCss.footer}>
          <button
            type="button"
            style={{
              ...modalCss.btn,
              ...modalCss.cancelBtn,
              ...(loading ? modalCss.btnDisabled : {}),
            }}
            onClick={onClose}
            disabled={loading}
          >
            {t.cancel || "Cancel"}
          </button>
          <button
            type="button"
            style={{
              ...modalCss.btn,
              ...modalCss.confirmBtn,
              ...(loading ? modalCss.btnDisabled : {}),
            }}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading
              ? t.submitting || "Submitting..."
              : t.yesReject || "Yes, Reject"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ==================================================================
   Main View component
   ================================================================== */
const ViewLessonPlanApproval = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: lessonPlanId } = useParams();
  const location = useLocation();

  const user = TokenService.getUser();
  const { translations } = useSelector((state) => state.language);
  const t = translations?.labels || translations || {};

  const clientData = user?.client_data?.[0] || {};
  const userId = user?.id || "";
  const roleId = Number(user?.role?.id);
  const school_id = clientData?.school_id || "";
  const academic_year = clientData?.academic_year || "";
  const master_id = clientData?.master_id || "";
  const updated_by = Number(user?.id) || 0;

  // Resolve def_id / item_id from router state (preferred), then params
  const routeState = location.state || {};
  const routeRow = routeState.data || {};
  const def_id =
    routeState.def_id ??
    routeRow.def_id ??
    routeState.ID ??
    lessonPlanId ??
    null;
  const item_id =
    routeState.item_id ??
    routeRow.item_id ??
    null;
  const id =
    routeState.hm_id ??
    routeRow.hm_id ??
    null;
  // Lock signal from the list page. The list already knows whether a row
  // is in a terminal state (Approved/Rejected/Completed) and passes the
  // flag explicitly via navigate state — no guesswork here.
  const routeIsLocked =
    routeState.isLocked === true ||
    ["approved", "rejected", "completed"].includes(
      String(routeState.status_text || routeRow.status_text || "").toLowerCase()
    ) ||
    [2, 3, 5].includes(Number(routeState.status ?? routeRow.status));

  // ------------------------------------------------------------------
  // State
  // ------------------------------------------------------------------
  const [modal, setModal] = useState({ open: false, type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [rejectModal, setRejectModal] = useState({ open: false, row: null });
  const [submitting, setSubmitting] = useState(false);

  // Locks Approve/Reject buttons. Two ways this becomes true:
  //   1. The list page told us the row is in a terminal state (Approved /
  //      Rejected / Completed) via `routeIsLocked` — seeded into the initial
  //      state below so the buttons start disabled with no flash of orange.
  //   2. The HM successfully submits an Approve/Reject from THIS page —
  //      `setActionLocked(true)` inside `callUpdateStatusApi` prevents
  //      double-submits in the 1s before navigating back.
  //
  // We deliberately do NOT re-derive the lock from `/book/view-lesson-plan`'s
  // response, because that endpoint can return a status that diverges from
  // the list's view of the row.
  const [actionLocked, setActionLocked] = useState(routeIsLocked);

  const [headerData, setHeaderData] = useState({
    book: "",
    class: "",
    subject: "",
    medium: "",
    topic: "",
    subTopic: "",
    startPage: "",
    endPage: "",
    scheduleDate: "",
    period: "",
    teacherName: "",
  });

  // Raw record from /book/view-lesson-plan — used as rowData for status API
  const [rowData, setRowData] = useState(null);

  const [lessonPlanContent, setLessonPlanContent] = useState("");
  const [staffComment, setStaffComment] = useState("");

  // ------------------------------------------------------------------
  // API: POST /book/view-lesson-plan
  // ------------------------------------------------------------------
  const fetchLessonPlanDetails = useCallback(async () => {
    if (!def_id && !item_id) {
      setModal({
        open: true,
        type: "failed",
        message: t.missingIds || "Missing lesson plan identifiers",
      });
      return;
    }

    setLoading(true);
    try {
      const payload = { def_id, item_id,id };
      const response = await AxiosInstance.post(
        `/book/view-lesson-plan`,
        payload
      );

      // API returns the record directly (sometimes under data.data, sometimes data)
      const raw = response?.data?.data || response?.data || {};

      // Accept both "wrapped" { status: true, data: {...} } and raw object shapes
      const record =
        raw && typeof raw === "object" && raw.def_id !== undefined
          ? raw
          : raw?.data || {};

      if (!record || Object.keys(record).length === 0) {
        setModal({
          open: true,
          type: "failed",
          message:
            response?.data?.message ||
            t.somethingWentWrong ||
            "Something went wrong",
        });
        return;
      }

      // Hold the raw record so Approve/Reject can send it to the status API
      setRowData(record);

      // NOTE: We intentionally do NOT call setActionLocked(true) here based
      // on `record.status`. The list page is the source of truth for whether
      // this row is editable; it passes `isLocked` / `status_text` via
      // navigate state and that has already seeded `actionLocked`.

      setHeaderData({
        book: record.book_name || "",
        class: record.class_name || "",
        subject: record.subject_name || "",
        medium: record.medium || "",
        topic: record.topic || "",
        subTopic: record.sub_topic || "",
        startPage:
          record.start_page !== undefined && record.start_page !== null
            ? String(record.start_page)
            : "",
        endPage:
          record.end_page !== undefined && record.end_page !== null
            ? String(record.end_page)
            : "",
        scheduleDate: record.schedule_date
          ? moment(record.schedule_date).format("DD/MM/YYYY")
          : record.scheduled_date
            ? moment(record.scheduled_date).format("DD/MM/YYYY")
            : "",
        period: record.periods || record.period || "",
        teacherName: record.staff_name || record.teacher_name || "",
      });

      setLessonPlanContent(record.content || "");
      setStaffComment(record.staff_comment || "");
    } catch (error) {
      console.error("View lesson plan fetch error:", error);
      setModal({
        open: true,
        type: "failed",
        message: t.somethingWentWrong || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  }, [def_id, item_id, t]);

  useEffect(() => {
    fetchLessonPlanDetails();
  }, [fetchLessonPlanDetails]);

  // ------------------------------------------------------------------
  // API: PUT /book/update-lesson-plan-status
  // Same signature and payload shape as the list page.
  // ------------------------------------------------------------------
  const callUpdateStatusApi = useCallback(
    async ({ rowData: row, status, comments = "" }) => {
      const def_id = row?.def_id;
      const hm_id = row?.id;

      const payload = {
        def_id,
        status, // 2 = Approve, 3 = Reject
        comments,
        hm_id,
        updated_by,
      };

      try {
        setSubmitting(true);
        const response = await AxiosInstance.put(
          `/book/update-lesson-plan-status`,
          payload
        );

        if (response?.data?.status) {
          // Lock buttons immediately on success — prevents double-submits during
          // the 1s window before navigate(-1) and handles the case where
          // navigation is delayed or blocked.
          setActionLocked(true);

          setModal({
            open: true,
            type: "success",
            message:
              response?.data?.message ||
              (status === 2
                ? t.approvedSuccessfully || "Approved successfully"
                : t.rejectedSuccessfully || "Rejected successfully"),
          });
          // Go back to the list after a short beat so it can refetch
          setTimeout(() => {
            navigate(-1);
          }, 1000);
        } else {
          console.error("Status update failed:", response?.data?.message);
          setModal({
            open: true,
            type: "failed",
            message:
              response?.data?.message ||
              t.somethingWentWrong ||
              "Something went wrong",
          });
        }
      } catch (e) {
        console.error("Status update error:", e);
        setModal({
          open: true,
          type: "failed",
          message: t.somethingWentWrong || "Something went wrong",
        });
      } finally {
        setSubmitting(false);
      }
    },
    [updated_by, t, navigate]
  );

  // ------------------------------------------------------------------
  // Handlers
  // ------------------------------------------------------------------
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Approve => call API immediately (status 2)
  const handleApprove = useCallback(() => {
    if (!rowData) return;
    callUpdateStatusApi({ rowData, status: 2, comments: "" });
  }, [rowData, callUpdateStatusApi]);

  // Reject => open modal (status 3 handled on confirm)
  const handleReject = useCallback(() => {
    if (!rowData) return;
    setRejectModal({ open: true, row: rowData });
  }, [rowData]);

  const handleRejectConfirm = async (reason) => {
    if (!rejectModal.row) return;
    await callUpdateStatusApi({
      rowData: rejectModal.row,
      status: 3,
      comments: reason,
    });
    setRejectModal({ open: false, row: null });
  };

  const handleRejectCancel = () => {
    setRejectModal({ open: false, row: null });
  };

  const closeModal = () => setModal({ open: false, type: "", message: "" });

  // Render a label/value pair
  const Field = ({ label, value }) => (
    <div className={styles.fieldBlock}>
      <div className={styles.fieldLabel}>{label}</div>
      <div className={styles.fieldValue}>{value || "-"}</div>
    </div>
  );

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <div className={styles.pageWrapper}>
      {/* ============ Main Card ============ */}
      <div className={styles.card}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaRegBuilding />
          </div>
          <h2 className={styles.headerTitle}>
            {t.viewLessonPlan || "View Lesson Plan"}
          </h2>
        </div>

        {loading ? (
          <div style={{ padding: 24 }}>{t.loading || "Loading..."}</div>
        ) : (
          <>
            {/* Row 1: Book / Class / Subject / Medium */}
            <div className={styles.fieldsGrid}>
              <Field label={t.book || "Book"} value={headerData.book} />
              <Field label={t.class || "Class"} value={headerData.class} />
              <Field label={t.subject || "Subject"} value={headerData.subject} />
              <Field label={t.medium || "Medium"} value={headerData.medium} />

              {/* Row 2: Topic / Sub Topic / Start Page / End Page */}
              <Field label={t.topic || "Topic"} value={headerData.topic} />
              <Field
                label={t.subTopic || "Sub Topic"}
                value={headerData.subTopic}
              />
              <Field
                label={t.startPageLabel || "Start Page"}
                value={headerData.startPage}
              />
              <Field
                label={t.endPageLabel || "End Page"}
                value={headerData.endPage}
              />

              {/* Row 3: Schedule Date / Period / Teacher Name */}
              <Field
                label={t.scheduleDate || "Schedule Date"}
                value={headerData.scheduleDate}
              />
              <Field label={t.period || "Period"} value={headerData.period} />
              <Field
                label={t.teacherName || "Teacher Name"}
                value={headerData.teacherName}
              />
              <div />
            </div>

            {/* Lesson Plan Section */}
            <div className={styles.lessonPlanSection}>
              <div className={styles.lessonPlanLabel}>
                {t.lessonPlan || "Lesson Plan"}
              </div>
              <div
                className={styles.lessonPlanContent}
                dangerouslySetInnerHTML={{ __html: lessonPlanContent }}
              />
            </div>

            {/* Staff Comment (shown when present, e.g. rejected plans) */}
            {staffComment ? (
              <div className={styles.lessonPlanSection}>
                <div className={styles.lessonPlanLabel}>
                  {t.staffComment || "Staff Comment"}
                </div>
                <div className={styles.fieldValue}>{staffComment}</div>
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* ============ Action Buttons ============ */}
      <div className={styles.actionBar}>
        <Button
          className={`${styles.actionButton} ${styles.cancelButton}`}
          onClick={handleBack}
          disabled={submitting}
        >
          {t.cancel || "Cancel"}
        </Button>

        {/*
          When disabled, render a plain styled <div> instead of the MUI
          <Button>. This completely sidesteps any CSS rule that forces the
          .approveButton / .rejectButton background to orange regardless of
          the disabled state. When enabled, render the normal MUI button.
        */}
        {loading || submitting || actionLocked ? (
          <div
            className={styles.actionButton}
            aria-disabled="true"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 100,
              height: 40,
              padding: "0 16px",
              borderRadius: 4,
              fontSize: 14,
              fontWeight: 500,
              backgroundColor: "#d1d5db",
              color: "#ffffff",
              cursor: "not-allowed",
              userSelect: "none",
              pointerEvents: "none",
              opacity: 0.75,
              textTransform: "uppercase",
            }}
          >
            {submitting
              ? t.submitting || "Submitting..."
              : t.approve || "Approve"}
          </div>
        ) : (
          <Button
            className={`${styles.actionButton} ${styles.approveButton}`}
            onClick={handleApprove}
          >
            {t.approve || "Approve"}
          </Button>
        )}

        {loading || submitting || actionLocked ? (
          <div
            className={styles.actionButton}
            aria-disabled="true"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 100,
              height: 40,
              padding: "0 16px",
              borderRadius: 4,
              fontSize: 14,
              fontWeight: 500,
              backgroundColor: "#d1d5db",
              color: "#ffffff",
              cursor: "not-allowed",
              userSelect: "none",
              pointerEvents: "none",
              opacity: 0.75,
              textTransform: "uppercase",
            }}
          >
            {t.reject || "Reject"}
          </div>
        ) : (
          <Button
            className={`${styles.actionButton} ${styles.rejectButton}`}
            onClick={handleReject}
          >
            {t.reject || "Reject"}
          </Button>
        )}
      </div>

      {/* Inline rejection modal */}
      <RejectionReasonModal
        open={rejectModal.open}
        onClose={handleRejectCancel}
        onConfirm={handleRejectConfirm}
        loading={submitting}
        translations={translations}
      />
    </div>
  );
};

export default ViewLessonPlanApproval;
